import { getApiServerUrl } from '../utils/config';

const API_BASE_URL = getApiServerUrl();

const getToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

const setToken = (token: string): void => {
  localStorage.setItem('accessToken', token);
};

const removeToken = (): void => {
  localStorage.removeItem('accessToken');
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
};

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (response.status === 401 || response.status === 403) {
      removeToken();
      window.location.href = '/admin';
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

export const apiGet = (endpoint: string) => apiRequest(endpoint);

export const apiPost = (endpoint: string, data: any) =>
  apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiPut = (endpoint: string, data: any) =>
  apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const apiDelete = (endpoint: string) =>
  apiRequest(endpoint, {
    method: 'DELETE',
  });

export const apiUpload = async (
  endpoint: string,
  file: File,
  onProgress?: (progress: number) => void,
) => {
  const formData = new FormData();
  formData.append('file', file);

  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject(new Error('Failed to parse response.'));
        }
      } else if (xhr.status === 401 || xhr.status === 403) {
        removeToken();
        window.location.href = '/admin';
        reject(new Error('Authentication expired. Please log in again.'));
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error.'));
    });

    xhr.open('POST', url);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
};

export const authApi = {
  login: async (username: string, password: string) => {
    const response = await apiPost('/auth/login', { username, password });

    if (response.success && response.token) {
      setToken(response.token);
    }

    return response;
  },
  logout: async () => {
    try {
      await apiPost('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeToken();
    }
    return { success: true, message: 'Logged out.' };
  },
  verify: () => apiGet('/auth/verify'),
  refresh: () => apiPost('/auth/refresh', {}),
  getToken: () => getToken(),
  isTokenExpired: () => {
    const token = getToken();
    return token ? isTokenExpired(token) : true;
  },
};

export const contentApi = {
  get: (section: string) => apiGet(`/content/${section}`),
  update: (section: string, data: any) => apiPut(`/content/${section}`, data),
  create: (section: string, data: any) => apiPost(`/content/${section}`, data),
  delete: (section: string) => apiDelete(`/content/${section}`),
  getContent: (section: string) => apiGet(`/content/${section}`),
  saveContent: (section: string, sections: any) => apiPost(`/content/${section}`, { sections }),
};

export const portfolioApi = {
  getAll: () => apiGet('/portfolio'),
  getById: (id: string) => apiGet(`/portfolio/${id}`),
  create: (data: any) => apiPost('/portfolio', data),
  update: (id: string, data: any) => apiPut(`/portfolio/${id}`, data),
  delete: (id: string) => apiDelete(`/portfolio/${id}`),
  getPortfolioData: () => apiGet('/portfolio'),
  getHomePortfolioData: () => apiGet('/portfolio/home'),
  saveHomePortfolioData: (data: any) => apiPost('/portfolio/home', data),
  updateHomePortfolioData: (data: any) => apiPut('/portfolio/home', data),
  savePortfolioData: (data: any) => apiPost('/portfolio', { tabs: data }),
  resetPortfolioData: () => apiDelete('/portfolio'),
  deleteHomePortfolioCard: (cardId: string) => apiDelete(`/portfolio/home/card/${cardId}`),
};

export const aboutApi = {
  get: () => apiGet('/about'),
  update: (data: any) => apiPut('/about', data),
  getAboutData: () => apiGet('/about'),
  saveAboutData: (data: any) => apiPost('/about', data),
};

export const sidebarApi = {
  get: () => apiGet('/sidebar'),
  update: (data: any) => apiPut('/sidebar', data),
  getSidebarData: () => apiGet('/sidebar'),
  saveSidebarData: (data: any) => apiPost('/sidebar', data),
};

export const projectTypeApi = {
  getAll: () => apiGet('/project-type'),
  getById: (id: string) => apiGet(`/project-type/${id}`),
  create: (data: any) => apiPost('/project-type', data),
  update: (id: string, data: any) => apiPut(`/project-type/${id}`, data),
  delete: (id: string) => apiDelete(`/project-type/${id}`),
  getProjectTypeData: () => apiGet('/project-type'),
  saveProjectTypeData: (data: any) => apiPost('/project-type', { items: data }),
};

export const uploadApi = {
  uploadFile: (file: File, onProgress?: (progress: number) => void) =>
    apiUpload('/upload', file, onProgress),
  getMediaList: (params?: {
    type?: 'all' | 'image' | 'video';
    search?: string;
    sortBy?: 'date' | 'name' | 'size';
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.append('type', params.type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    return apiGet(`/upload/list${queryString ? `?${queryString}` : ''}`);
  },
  updateMediaInfo: (
    type: string,
    filename: string,
    data: {
      name?: string;
      description?: string;
      tags?: string[];
    },
  ) => apiPut(`/upload/${type}/${filename}`, data),
  deleteFile: (type: string, filename: string) => apiDelete(`/upload/${type}/${filename}`),
};
